import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const GestionReunionesProfesor = () => {
  const { user, API } = useContext(AuthContext);
  const [reuniones, setReuniones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    link_video: '',
    fecha: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (user?.rol === 'admin') {
      cargarReuniones();
    }
  }, [user]);

  const cargarReuniones = async () => {
    try {
      const response = await axios.get(`${API}/admin/reuniones-profesores`);
      setReuniones(response.data.reuniones || []);
    } catch (error) {
      console.error('Error cargando reuniones:', error);
      toast.error('Error al cargar las reuniones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editando) {
        // Actualizar
        await axios.put(`${API}/admin/reuniones-profesores/${editando.id}`, formData);
        toast.success('Reunión actualizada correctamente');
      } else {
        // Crear nueva (con notificación push)
        await axios.post(`${API}/admin/reuniones-profesores`, formData);
        toast.success('Reunión creada y notificación enviada a profesores');
      }
      
      setIsDialogOpen(false);
      resetForm();
      cargarReuniones();
    } catch (error) {
      console.error('Error guardando reunión:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la reunión');
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta reunión?')) return;
    
    try {
      await axios.delete(`${API}/admin/reuniones-profesores/${id}`);
      toast.success('Reunión eliminada correctamente');
      cargarReuniones();
    } catch (error) {
      console.error('Error eliminando reunión:', error);
      toast.error('Error al eliminar la reunión');
    }
  };

  const abrirEditar = (reunion) => {
    setEditando(reunion);
    setFormData({
      titulo: reunion.titulo,
      descripcion: reunion.descripcion || '',
      link_video: reunion.link_video,
      fecha: reunion.fecha ? new Date(reunion.fecha).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditando(null);
    setFormData({
      titulo: '',
      descripcion: '',
      link_video: '',
      fecha: new Date().toISOString().split('T')[0]
    });
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Solo administradores pueden acceder a esta sección</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-center">
        <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
        <p className="text-gray-600">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <i className="fas fa-video text-blue-600"></i>
            Gestión de Reuniones de Profesores
          </h1>
          <p className="text-gray-600 mt-2">
            Administra los videos de reuniones visibles para profesores
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <i className="fas fa-plus mr-2"></i>
          Nueva Reunión
        </Button>
      </div>

      {/* Lista de Reuniones */}
      {reuniones.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <i className="fas fa-video-slash text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg mb-4">No hay reuniones creadas</p>
            <Button onClick={() => setIsDialogOpen(true)} className="bg-blue-600">
              <i className="fas fa-plus mr-2"></i>
              Crear Primera Reunión
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {reuniones.map((reunion) => (
            <Card key={reunion.id} className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl text-gray-900 flex items-center gap-2">
                      <i className="fas fa-play-circle text-blue-600"></i>
                      {reunion.titulo}
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-2">
                      <i className="fas fa-calendar-alt mr-2"></i>
                      {formatFecha(reunion.fecha)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => abrirEditar(reunion)}
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      <i className="fas fa-edit mr-1"></i>
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEliminar(reunion.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <i className="fas fa-trash mr-1"></i>
                      Eliminar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {reunion.descripcion && (
                  <p className="text-gray-700 mb-3 whitespace-pre-line">{reunion.descripcion}</p>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <i className="fas fa-link text-gray-400"></i>
                  <a 
                    href={reunion.link_video} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate"
                  >
                    {reunion.link_video}
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Crear/Editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editando ? 'Editar Reunión' : 'Nueva Reunión de Profesores'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="titulo">Título *</Label>
              <Input
                id="titulo"
                value={formData.titulo}
                onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                placeholder="Ej: Reunión Pedagógica Enero 2025"
                required
              />
            </div>

            <div>
              <Label htmlFor="fecha">Fecha de la Reunión *</Label>
              <Input
                id="fecha"
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                required
              />
            </div>

            <div>
              <Label htmlFor="link_video">Link del Video *</Label>
              <Input
                id="link_video"
                type="url"
                value={formData.link_video}
                onChange={(e) => setFormData({...formData, link_video: e.target.value})}
                placeholder="https://youtube.com/watch?v=... o https://drive.google.com/..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Soporta: YouTube, Google Drive, Zoom recordings
              </p>
            </div>

            <div>
              <Label htmlFor="descripcion">Descripción (Opcional)</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                placeholder="Temas tratados, acuerdos, etc."
                rows={4}
              />
            </div>

            {!editando && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <i className="fas fa-bell"></i>
                  Al crear esta reunión, se enviará una notificación push a todos los profesores
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <i className="fas fa-save mr-2"></i>
                {editando ? 'Actualizar' : 'Crear y Notificar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionReunionesProfesor;
